import { useEffect, useMemo, useRef, useState } from "react";
import type * as type from "@vTypes";
import "@css/manpowerCalendar.css";
import { cn } from "@/utils/cn";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function Comp_manpowerCalendar({
  holidayDates,
  defaultShiftCode,
  activeDate,
  manPower_daily,
  setManpower_daily,
  manPower_monthly,
  filterExceed,
  filterBelow,
  showMaxManpower,
}: {
  holidayDates: string[];
  defaultShiftCode: type.availableShifts[];
  activeDate: { year: number; month: number };
  manPower_daily: type.ManPowerDaily;
  setManpower_daily: React.Dispatch<React.SetStateAction<type.ManPowerDaily>>;
  manPower_monthly: type.ManPowerMonthly;
  filterExceed: boolean;
  filterBelow: boolean;
  showMaxManpower: boolean;
}) {
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const lastDay = new Date(activeDate.year, activeDate.month, 0);
  const daysInMonth = lastDay.getDate();
  const [selectedWeekdayIndex, setSelectedWeekdayIndex] = useState<
    number | null
  >(null);
  const [focusedBulkTarget, setFocusedBulkTarget] = useState<{
    shiftKey: string;
    kind: "min" | "max";
  } | null>(null);

  const firstDay = new Date(activeDate.year, activeDate.month - 1, 1);
  const startDay = (firstDay.getDay() + 6) % 7;
  const displayShifts = useMemo(
    () => defaultShiftCode?.filter((s) => s && s.default_shift !== "O") ?? [],
    [defaultShiftCode]
  );

  const getCellManpowerHighlight = (
    manpower: number,
    defaultManpower: number
  ) => {
    if (filterExceed && manpower > defaultManpower) {
      return "bg-manpower-exceed-bg border-manpower-exceed-border";
    }
    if (filterBelow && manpower < defaultManpower) {
      return "bg-manpower-below-bg border-manpower-below-border";
    }
    return "border-border/50 bg-white";
  };

  const calendarCells = useMemo(() => {
    const total = 42;
    const prevLastDay = new Date(
      activeDate.year,
      activeDate.month - 1,
      0
    ).getDate();
    const list: Array<{
      day: number;
      isCurrent: boolean;
      monthOffset: -1 | 0 | 1;
    }> = [];
    for (let i = 0; i < startDay; i++) {
      list.push({
        day: prevLastDay - startDay + i + 1,
        isCurrent: false,
        monthOffset: -1,
      });
    }
    for (let day = 1; day <= daysInMonth; day++) {
      list.push({ day, isCurrent: true, monthOffset: 0 });
    }
    let nextDay = 1;
    while (list.length < total) {
      list.push({ day: nextDay++, isCurrent: false, monthOffset: 1 });
    }
    return list;
  }, [activeDate.year, activeDate.month, daysInMonth, startDay]);

  const weekdayLabels = ["월", "화", "수", "목", "금", "토", "일"];

  const shiftColumnCount = Math.max(displayShifts.length, 1);
  const shiftBlockGridStyle = useMemo(
    () =>
      ({
        gridTemplateColumns: `minmax(0, max-content) repeat(${shiftColumnCount}, minmax(0, 1fr))`,
      }) as const,
    [shiftColumnCount]
  );

  const dayColorClass = (isCurrent: boolean, day: number, idx: number) => {
    if (!isCurrent) return "text-[#D6D6DE]";
    const dow = idx % 7;
    const dateStr = `${activeDate.year}-${String(activeDate.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    if (holidayDates.includes(dateStr)) return "text-text-holiday";
    if (dow === 5) return "text-text-sat";
    if (dow === 6) return "text-text-sun";
    return "text-[#333333]";
  };

  const updateDailyValue = (
    shiftKey: string,
    day: number,
    value: number,
    kind: "min" | "max"
  ) => {
    const targetKey = kind === "max" ? `${shiftKey}_max` : shiftKey;
    setManpower_daily((prev) => {
      const current = [
        ...(prev[targetKey] ?? Array.from({ length: daysInMonth }, () => 0)),
      ];

      if (selectedWeekdayIndex === null) {
        current[day - 1] = value;
      } else {
        for (let targetDay = 1; targetDay <= daysInMonth; targetDay++) {
          const weekdayIndex = (startDay + targetDay - 1) % 7;
          if (weekdayIndex === selectedWeekdayIndex) {
            current[targetDay - 1] = value;
          }
        }
      }

      if (kind !== "min" || !showMaxManpower) {
        return { ...prev, [targetKey]: current };
      }

      const maxKey = `${shiftKey}_max`;
      const maxValues = [
        ...(prev[maxKey] ?? Array.from({ length: daysInMonth }, () => 0)),
      ];
      const adjustMaxValue = (targetDay: number) => {
        const idx = targetDay - 1;
        const maxValue = Number(maxValues[idx] ?? 0);
        if (maxValue > 0 && maxValue < value) {
          maxValues[idx] = value;
        }
      };

      if (selectedWeekdayIndex === null) {
        adjustMaxValue(day);
      } else {
        for (let targetDay = 1; targetDay <= daysInMonth; targetDay++) {
          const weekdayIndex = (startDay + targetDay - 1) % 7;
          if (weekdayIndex === selectedWeekdayIndex) {
            adjustMaxValue(targetDay);
          }
        }
      }

      return { ...prev, [targetKey]: current, [maxKey]: maxValues };
    });
  };

  const updateMinValue = (shiftKey: string, day: number, value: number) => {
    updateDailyValue(shiftKey, day, value, "min");
  };

  const updateMaxValue = (shiftKey: string, day: number, value: number) => {
    updateDailyValue(shiftKey, day, value, "max");
  };

  const isBulkTargetActive = (
    weekdayIndex: number,
    shiftKey: string,
    kind: "min" | "max"
  ) => {
    return (
      selectedWeekdayIndex === weekdayIndex &&
      focusedBulkTarget?.shiftKey === shiftKey &&
      focusedBulkTarget.kind === kind
    );
  };

  const isBulkShiftActive = (weekdayIndex: number, shiftKey: string) => {
    return (
      selectedWeekdayIndex === weekdayIndex &&
      focusedBulkTarget?.shiftKey === shiftKey
    );
  };

  const handleBulkInputFocus = (
    weekdayIndex: number,
    shiftKey: string,
    kind: "min" | "max"
  ) => {
    if (
      selectedWeekdayIndex !== null &&
      selectedWeekdayIndex !== weekdayIndex
    ) {
      setSelectedWeekdayIndex(weekdayIndex);
    }
    setFocusedBulkTarget({ shiftKey, kind });
  };

  useEffect(() => {
    if (selectedWeekdayIndex === null) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && calendarRef.current?.contains(target)) return;
      setSelectedWeekdayIndex(null);
      setFocusedBulkTarget(null);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [selectedWeekdayIndex]);

  return (
    <div ref={calendarRef} className="rounded-[10px] border ">
      <div className="grid grid-cols-7 border-b ">
        {weekdayLabels.map((label, idx) => (
          <Tooltip key={`${label}-${idx}`}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() =>
                  setSelectedWeekdayIndex((prev) => {
                    const next = prev === idx ? null : idx;
                    if (next === null) {
                      setFocusedBulkTarget(null);
                    }
                    return next;
                  })
                }
                className={cn(
                  "py-[7px] text-center text-[14px] font-semibold border-r outline-none transition-colors hover:underline underline-offset-4 focus-visible:ring-2 focus-visible:ring-sig/30 focus-visible:ring-inset",
                  selectedWeekdayIndex === idx && "bg-accent",
                  idx === 5
                    ? "text-text-sat"
                    : idx === 6
                      ? "text-text-sun"
                      : "text-[#555]",
                  weekdayLabels.length - 1 === idx && "border-r-0"
                )}
              >
                {label}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="whitespace-nowrap">
              요일별 근무코드 일괄적용
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {calendarCells.map((cell, idx) => (
          <div
            key={`${cell.monthOffset}-${cell.day}-${idx}`}
            className={cn(
              "min-w-0 border-r border-b min-h-[80px] px-[6px] py-[5px] transition-colors [&:nth-child(7n)]:border-r-0",
              selectedWeekdayIndex === idx % 7 && "bg-accent"
            )}
          >
            <div
              className={cn(
                "mb-[4px] mt-[4px] ml-[2px] text-left text-sm leading-none",
                dayColorClass(cell.isCurrent, cell.day, idx),
                cell.isCurrent ? "font-medium" : "font-light"
              )}
            >
              {cell.day}
            </div>
            {cell.isCurrent && (
              <div className="w-full min-w-0">
                <div
                  className="grid w-full min-w-0 items-center gap-x-[2px] gap-y-[2px] text-[#999]"
                  style={shiftBlockGridStyle}
                >
                  <span className="select-none" aria-hidden />
                  {displayShifts.map((shift) => {
                    const shiftKey = shift.default_shift || shift.shift_id;
                    return (
                      <span
                        key={`${cell.day}-${shift.shift_id}-label`}
                        className={cn(
                          "min-w-0 rounded-[4px] text-center text-sm font-semibold leading-none transition-colors",
                          isBulkShiftActive(idx % 7, shiftKey) && " text-sig"
                        )}
                      >
                        {shift.shift_id}
                      </span>
                    );
                  })}

                  <span className="order-2 whitespace-nowrap text-xs leading-none text-muted-foreground px-1">
                    최소
                  </span>
                  {displayShifts.map((shift) => {
                    const shiftKey = shift.default_shift || shift.shift_id;
                    const dayIndex = cell.day - 1;
                    const minValue = Number(
                      manPower_daily?.[shiftKey]?.[dayIndex] ?? 0
                    );
                    const defaultValue = Number(
                      manPower_monthly?.[
                        shiftKey as keyof type.ManPowerMonthly
                      ] ?? 0
                    );
                    return (
                      <input
                        key={`${cell.day}-min-${shiftKey}`}
                        value={minValue}
                        onChange={(e) => {
                          const parsed = Number(
                            e.target.value.replace(/\D/g, "") || 0
                          );
                          updateMinValue(shiftKey, cell.day, parsed);
                        }}
                        onFocus={() =>
                          handleBulkInputFocus(idx % 7, shiftKey, "min")
                        }
                        className={cn(
                          "order-2 size-8 h-7 max-w-full rounded-[4px] border px-0 text-center text-sm tabular-nums text-[#333] outline-none focus:border-sig focus:ring-1 focus:ring-sig/20",
                          getCellManpowerHighlight(minValue, defaultValue),
                          isBulkTargetActive(idx % 7, shiftKey, "min") &&
                            "border-sig bg-sig/5 ring-2 ring-sig/20"
                        )}
                      />
                    );
                  })}

                  {showMaxManpower && (
                    <>
                      <span className="order-1 whitespace-nowrap text-xs leading-none text-muted-foreground px-1">
                        최대
                      </span>
                      {displayShifts.map((shift) => {
                        const shiftKey = shift.default_shift || shift.shift_id;
                        const dayIndex = cell.day - 1;
                        const defaultMinValue = Number(
                          manPower_monthly?.[
                            shiftKey as keyof type.ManPowerMonthly
                          ] ?? 0
                        );
                        const defaultMaxValue = Number(
                          manPower_monthly?.[
                            `${shiftKey}_max` as keyof type.ManPowerMonthly
                          ] ?? defaultMinValue
                        );
                        const maxValue = Number(
                          manPower_daily?.[`${shiftKey}_max`]?.[dayIndex] ??
                            defaultMaxValue
                        );
                        return (
                          <input
                            key={`${cell.day}-max-${shiftKey}`}
                            value={maxValue === 0 ? "-" : maxValue}
                            onChange={(e) => {
                              const parsed = Number(
                                e.target.value.replace(/\D/g, "") || 0
                              );
                              updateMaxValue(shiftKey, cell.day, parsed);
                            }}
                            onBlur={() => {
                              if (maxValue > 0 && maxValue < minValue) {
                                updateMaxValue(
                                  shiftKey,
                                  cell.day,
                                  minValue
                                );
                              }
                            }}
                            onFocus={() =>
                              handleBulkInputFocus(idx % 7, shiftKey, "max")
                            }
                            className={cn(
                              "order-1 size-8 h-7 max-w-full rounded-[4px] border px-0 text-center text-sm tabular-nums text-[#333] outline-none focus:border-sig focus:ring-1 focus:ring-sig/20",
                              getCellManpowerHighlight(
                                maxValue,
                                defaultMaxValue
                              ),
                              maxValue === 0 && "border-border/50 bg-white",
                              isBulkTargetActive(idx % 7, shiftKey, "max") &&
                                "border-sig bg-sig/5 ring-2 ring-sig/20"
                            )}
                          />
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
